import type { Types } from "@amplitude/analytics-browser";
import { WebViewDetector } from "../../utils/WebViewDetector";

const TRANSFER_PARAMS = ["ampDeviceId", "ampSessionId", "ampTimestamp"];

interface SessionTransfer {
    deviceId?: string;
    sessionId?: number;
}

let incomingTransfer: SessionTransfer = {};

const replaceUrl = (url: URL): void => {
    if (url.href !== window.location.href) {
        window.history.replaceState(window.history.state, "", url.href);
    }
};

// Consume the handoff before init(), rather than restoring IDs after events have started.
export const prepareAmplitudeSessionTransfer = (): SessionTransfer => {
    const url = new URL(window.location.href);
    const deviceId = url.searchParams.get("ampDeviceId");
    const sessionId = Number(url.searchParams.get("ampSessionId"));
    const expiresAt = Number(url.searchParams.get("ampTimestamp"));

    incomingTransfer = deviceId && Number.isSafeInteger(sessionId) && sessionId > 0 &&
        Number.isSafeInteger(expiresAt) && expiresAt > Date.now()
        ? { deviceId, sessionId }
        : {};

    // A reload must use the SDK's current session, not replay an old handoff URL.
    TRANSFER_PARAMS.forEach((param) => url.searchParams.delete(param));
    replaceUrl(url);
    return incomingTransfer;
};

// The transferred device takes precedence over older device IDs in u/localStorage.
export const getTransferredAmplitudeDeviceId = (): string | undefined => incomingTransfer.deviceId;

export const sessionTransferPlugin = (): Types.EnrichmentPlugin<Types.BrowserClient, Types.BrowserConfig> => {
    let config: Types.BrowserConfig;
    let client: Types.BrowserClient;

    const syncUrl = (): void => {
        if (!WebViewDetector.isRunningInWebView() || config.optOut) return;

        const { deviceId, sessionId, lastEventTime, sessionTimeout } = config;
        if (!deviceId || !sessionId || !lastEventTime) return;

        const url = new URL(window.location.href);
        const expiresAt = lastEventTime + sessionTimeout;
        if (expiresAt <= Date.now()) return;

        url.searchParams.set("ampDeviceId", deviceId);
        url.searchParams.set("ampSessionId", String(sessionId));
        url.searchParams.set("ampTimestamp", String(expiresAt));
        replaceUrl(url);
    };

    return {
        name: "webview-session-transfer",
        type: "enrichment",
        async setup(browserConfig, browserClient) {
            config = browserConfig;
            client = browserClient;
        },
        async onSessionIdChanged(sessionId) {
            if (sessionId !== incomingTransfer.sessionId) return;
            // setSessionId resets lastEventTime to the session's start time. An active
            // transferred session may be older than the inactivity timeout.
            await Promise.resolve();
            if (client.getSessionId() === sessionId) client.extendSession();
        },
        async execute(event) {
            try {
                // Read current SDK state, not a possibly queued event from the old session.
                syncUrl();
            } catch (error) {
                config.loggerProvider.warn(`Could not update Amplitude session transfer URL: ${String(error)}`);
            }
            return event;
        },
    };
};
